/**
 * Language selector component.
 * @module components/LanguageSelector/LanguageSelector
 *
 * - Changed dropdown for react-accessible-dropdown-menu-hook
 */

import React from 'react';
import { Link } from 'react-router-dom';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import find from 'lodash/find';

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import langmap from '@plone/volto/helpers/LanguageMap/LanguageMap';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { toReactIntlLang } from '@plone/volto/helpers/Utils/Utils';
import { Icon } from '@redturtle/iovisit-theme/components';

import config from '@plone/registry';

import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  switchLanguageTo: {
    id: 'Switch to',
    defaultMessage: 'Switch to',
  },
  languageSelector: {
    id: 'Select language',
    defaultMessage: 'Select language',
  },
  changeLanguage: {
    id: 'Change language',
    defaultMessage: 'Change language',
  },
});

const LanguageSelector = ({ onClickAction = () => {} }) => {
  const intl = useIntl();
  const currentLang = useSelector((state) => state.intl.locale);
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );

  const { settings } = config;

  const { buttonProps, itemProps, isOpen } = useDropdownMenu(
    settings.supportedLanguages.length,
  );

  return settings.isMultilingual ? (
    <div className="language-selector-wrapper">
      <div className="iv--language-selector">
        <div className="ui dropdown">
          <div
            {...buttonProps}
            aria-label={intl.formatMessage(messages.changeLanguage)}
          >
            <span className="text">{currentLang.toUpperCase()}</span>
            <Icon prefix="fas" icon="caret-down" className="icon" />
          </div>

          <div className={cx('menu', { visible: isOpen })} role="menu">
            {settings.supportedLanguages.map((lang, i) => {
              const translation = find(translations, { language: lang });

              return (
                <Link
                  {...itemProps[i]}
                  aria-label={`${intl.formatMessage(messages.switchLanguageTo)}
                  ${langmap[lang].nativeName}`}
                  className={cx('item', { selected: lang === currentLang })}
                  to={
                    translation
                      ? flattenToAppURL(translation['@id'])
                      : `/${lang}`
                  }
                  key={`language-selector-${lang}`}
                  onClick={() => {
                    if (lang !== currentLang) onClickAction();
                  }}
                >
                  {langmap[lang].nativeName}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <Helmet>
      <html lang={toReactIntlLang(settings.defaultLanguage)} />
    </Helmet>
  );
};

export default LanguageSelector;
